import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { partnerDdScoringApi } from '../../api/partner-dd-scoring';
import { ApiError } from '../../api/client';
import { useToast } from '../../components/Toast';

/**
 * Assessment & Scoring tab (spec sections 19-20, 58) - the heart of
 * the whole system. Every number shown here is genuinely computed
 * server-side; this component only displays what the API returns and
 * collects input, never calculates a score itself (explicit
 * direction: scoring logic never lives in React).
 */
export function PartnerDdCaseScoringTab({ ddCase }: { ddCase: { id: number; scoring_model_id: number | null } }) {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [scoreDrafts, setScoreDrafts] = useState<Record<number, { scoreValue: string; evidence: string; comment: string }>>({});
  const [selectedOverrides, setSelectedOverrides] = useState<string[]>([]);

  // Load the model this case will use (or the active one, if scoring hasn't started) so criteria are always shown.
  const modelsQuery = useQuery({ queryKey: ['dd-scoring-models'], queryFn: () => partnerDdScoringApi.listModels() });
  const activeModel = modelsQuery.data?.data.find((m) => m.status === 'active');
  const modelId = ddCase.scoring_model_id ?? activeModel?.id;
  const modelDetailQuery = useQuery({ queryKey: ['dd-scoring-model-detail', modelId], queryFn: () => partnerDdScoringApi.getModel(modelId as number), enabled: !!modelId });
  const scoresQuery = useQuery({ queryKey: ['dd-case-scores', ddCase.id], queryFn: () => partnerDdScoringApi.getCaseScores(ddCase.id) });
  const explanationQuery = useQuery({ queryKey: ['dd-case-explanation', ddCase.id], queryFn: () => partnerDdScoringApi.getExplanation(ddCase.id), retry: false });

  const saveScoreMutation = useMutation({
    mutationFn: (input: { criterionId: number; scoreValue: number; evidence?: string; comment?: string }) => partnerDdScoringApi.scoreCriterion(ddCase.id, input),
    onSuccess: () => { showSuccess('Score saved.'); queryClient.invalidateQueries({ queryKey: ['dd-case-scores', ddCase.id] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to save score.'),
  });

  const calculateMutation = useMutation({
    mutationFn: (isRecalculation: boolean) => partnerDdScoringApi.calculate(ddCase.id, { triggeredOverrideRuleCodes: selectedOverrides, isRecalculation }),
    onSuccess: () => {
      showSuccess('Score calculated.');
      queryClient.invalidateQueries({ queryKey: ['dd-case-explanation', ddCase.id] });
      setSelectedOverrides([]);
    },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to calculate score.'),
  });

  if (!modelDetailQuery.data) return <p className="muted">Loading scoring model…</p>;
  const { criteria, overrideRules } = modelDetailQuery.data.data;
  const scoresByCriterion = new Map((scoresQuery.data?.data ?? []).map((s) => [s.criterion_id, s]));

  return (
    <div>
      <div className="panel">
        <div className="panel-body">
          <h3>Score Each Criterion</h3>
          {criteria.map((c) => {
            const existing = scoresByCriterion.get(c.id);
            const draft = scoreDrafts[c.id] ?? { scoreValue: existing ? String(existing.score_value) : '', evidence: existing?.evidence ?? '', comment: existing?.reviewer_comment ?? '' };
            return (
              <div key={c.id} className="inline-form" style={{ borderBottom: '0.5px solid var(--border)', paddingBottom: 8, marginBottom: 8 }}>
                <label style={{ minWidth: 220 }}>
                  {c.name} ({c.weight_percent}%){Number(c.is_mandatory) === 1 && <span className="muted"> - mandatory</span>}
                  <select value={draft.scoreValue} onChange={(e) => setScoreDrafts((d) => ({ ...d, [c.id]: { ...draft, scoreValue: e.target.value } }))}>
                    <option value="">- score -</option>
                    {[0, 1, 2, 3, 4, 5].map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </label>
                <label>Evidence<input value={draft.evidence} onChange={(e) => setScoreDrafts((d) => ({ ...d, [c.id]: { ...draft, evidence: e.target.value } }))} /></label>
                <label>Comment<input value={draft.comment} onChange={(e) => setScoreDrafts((d) => ({ ...d, [c.id]: { ...draft, comment: e.target.value } }))} /></label>
                <button
                  disabled={!draft.scoreValue || saveScoreMutation.isPending}
                  onClick={() => saveScoreMutation.mutate({ criterionId: c.id, scoreValue: Number(draft.scoreValue), evidence: draft.evidence || undefined, comment: draft.comment || undefined })}
                >
                  Save
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <div className="panel">
        <div className="panel-body">
          <h3>Risk Overrides (only check what genuinely applies)</h3>
          {overrideRules.map((r) => (
            <label key={r.rule_code} style={{ display: 'block' }}>
              <input
                type="checkbox"
                checked={selectedOverrides.includes(r.rule_code)}
                onChange={(e) => setSelectedOverrides((prev) => e.target.checked ? [...prev, r.rule_code] : prev.filter((c) => c !== r.rule_code))}
              />
              {' '}{r.label} (forces {r.forces_risk_label} risk)
            </label>
          ))}
          <div style={{ marginTop: 12 }}>
            <button onClick={() => calculateMutation.mutate(!!explanationQuery.data)} disabled={calculateMutation.isPending}>
              {calculateMutation.isPending ? 'Calculating…' : explanationQuery.data ? 'Recalculate Score' : 'Calculate Score'}
            </button>
          </div>
        </div>
      </div>

      {explanationQuery.data && (
        <div className="panel">
          <div className="panel-body">
            <h2>Due Diligence Assessment</h2>
            <p style={{ fontSize: '2em', margin: '4px 0' }}>{explanationQuery.data.data.overallScore} / 100</p>
            <p><strong>{explanationQuery.data.data.finalRiskLabel.charAt(0).toUpperCase() + explanationQuery.data.data.finalRiskLabel.slice(1)} Risk</strong></p>
            <p>System Recommendation: {explanationQuery.data.data.recommendation}</p>
            <p className="muted">Approval Requirement: {explanationQuery.data.data.approvalRequirement}</p>

            <table className="data-table">
              <thead><tr><th>Criterion</th><th>Score</th><th>Max</th><th>Weight</th><th>Weighted Contribution</th><th>Evidence</th></tr></thead>
              <tbody>
                {explanationQuery.data.data.items.map((i) => (
                  <tr key={i.criterionId}>
                    <td>{i.criterionName}</td><td>{i.rawScore}</td><td>{i.maxScore}</td><td>{i.weightPercent}%</td><td>{i.weightedContribution}</td><td>{i.evidence ?? '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {explanationQuery.data.data.overridesApplied.length > 0 && (
              <p className="error-text">Risk Overrides Applied: {explanationQuery.data.data.overridesApplied.map((o) => o.label).join('; ')}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
